import { isDnf } from "model/Match/DriverTime";
import { MatchTeams } from "model/Match/MatchTeams";
import { RoundTime } from "model/Match/Track/RoundTime";
import {
  roundTeamHasAce,
  TrackBasedTrackRound,
  TrackRound,
} from "model/Match/Track/TrackRound";
import { getRosterId, getRosterDriverIds } from "model/Roster/Roster";

export const AFP_HEADER = "AFP";
export const FIRST_HEADER = "First";
export const AFPercent_HEADER = "AF%";
export const LAST_HEADER = "Last";
export const TOP50_HEADER = "Top Half";

export const AFP_TITLE =
  "Average Finish Position - Average Round Position (Includes DNFs)";
export const FIRST_TITLE = "First - Percentage Of Rounds Finished In First";
export const AFPercent_TITLE =
  "AF% - Average Finish Percentile (Includes DNFs)";
export const LAST_TITLE = "Last - Percentage Of Rounds Finished In Last";
export const TOP50_TITLE =
  "Top Half - Percentage Of Rounds Finished In A Top Half Position";

export const MIN_AFP = 0;
export const MAX_AFP = 1;

export const statRoundsAfp = (rounds: RoundTime[]) => {
  if (rounds.length === 0) {
    return undefined;
  }

  const numberOfRounds = rounds.length;
  const totalFinishPosition = rounds
    .map((r) => r.position)
    .reduce((a, b) => a + b, 0);
  return totalFinishPosition / numberOfRounds;
};

// returns a normalized value between 0 (all last) and 1 (all first)
export const statRoundsAfPercentile = (
  rounds: { time: RoundTime; lastPos: number }[]
) => {
  if (rounds.length === 0) {
    return undefined;
  }

  const numberOfRounds = rounds.length;
  const totalWeightedFpr = rounds
    .map((r) => {
      const position = isDnf(r.time) ? r.lastPos : r.time.position;
      return 1 - (position - 1) / (r.lastPos - 1);
    }) // Normalize finish position
    .reduce((sum, normalized) => sum + normalized, 0); // Sum all normalized values
  return totalWeightedFpr / numberOfRounds;
};

export const statRoundsFirstRate = (rounds: RoundTime[]) => {
  if (rounds.length === 0) {
    return { rate: undefined, breakdown: "0/0" };
  }

  const numberOfRounds = rounds.length;
  const numberOfFirsts = rounds.filter((r) => r.position === 1).length;
  const rate = numberOfFirsts / numberOfRounds;
  const breakdown = `${numberOfFirsts}/${numberOfRounds}`;
  return { rate, breakdown };
};

// todo - assumes 4 is last position
export const statRoundsLastRate = (
  rounds: { time: RoundTime; lastPos: number }[]
) => {
  if (rounds.length === 0) {
    return { rate: undefined, breakdown: "0/0" };
  }

  const numberOfRounds = rounds.length;
  const numberOfLasts = rounds.filter(
    (r) => r.time.position === r.lastPos || isDnf(r.time)
  ).length;
  const rate = numberOfLasts / numberOfRounds;
  const breakdown = `${numberOfLasts}/${numberOfRounds}`;
  return { rate, breakdown };
};

// todo - assumes 4 is last position
export const statRoundsTop50Rate = (
  rounds: { time: RoundTime; lastPos: number }[]
) => {
  if (rounds.length === 0) {
    return { rate: undefined, breakdown: "0/0" };
  }

  const numberOfRounds = rounds.length;
  const numberOfTop50 = rounds.filter(
    (r) => r.time.position <= r.lastPos / 2
  ).length;
  const rate = numberOfTop50 / numberOfRounds;
  const breakdown = `${numberOfTop50}/${numberOfRounds}`;
  return { rate, breakdown };
};

export const statRoundsBestCombinedTime = (
  rounds: TrackRound[],
  teams: MatchTeams
) => {
  // Group drivers by their roster
  const driversByRoster: { [rosterId: string]: string[] } = {};
  teams.rosters.forEach((roster) => {
    const rosterId = getRosterId(roster);
    const driverIds = getRosterDriverIds(roster, teams.scope) ?? [];
    if (rosterId && driverIds.length > 0) {
      driversByRoster[rosterId] = driverIds;
    }
  });

  // For each round, calculate the total time for each roster
  const rosterRoundTimes: {
    rosterId: string;
    totalTime: number;
    bestDriverTime: number;
  }[] = [];
  rounds.forEach((round) => {
    Object.entries(driversByRoster).forEach(([rosterId, driverIds]) => {
      // Get times for all drivers in this roster for this round
      const driverTimes = driverIds
        .map(
          (driverId) =>
            round.times.find((t) => t.driverId === driverId)?.finishTimeMs
        )
        .filter((time) => time !== undefined && time > 0) as number[];

      // Only count complete rounds where all drivers have a valid time
      if (driverTimes.length === driverIds.length) {
        const totalTime = driverTimes.reduce((sum, time) => sum + time, 0);
        const bestDriverTime = Math.min(...driverTimes);
        rosterRoundTimes.push({ rosterId, totalTime, bestDriverTime });
      }
    });
  });

  // Find the best (lowest) roster time
  if (rosterRoundTimes.length === 0) {
    return undefined;
  }

  const bestRosterTimeEntry = rosterRoundTimes.reduce((best, current) => {
    // If current total time is better than best total time
    if (current.totalTime < best.totalTime) {
      return current;
    }
    // If total times are tied, use the roster with the best driver time
    if (
      current.totalTime === best.totalTime &&
      current.bestDriverTime < best.bestDriverTime
    ) {
      return current;
    }
    return best;
  }, rosterRoundTimes[0]);

  return {
    rosterId: bestRosterTimeEntry.rosterId,
    bestTime: bestRosterTimeEntry.totalTime,
  };
};

export const statRoundsTeamAceCount = (
  rounds: TrackBasedTrackRound[],
  rosterDriverIds: string[]
) => {
  return rounds.filter((r) => roundTeamHasAce(r, rosterDriverIds)).length;
};

export const statRoundsTeamAceRate = (
  rounds: TrackBasedTrackRound[],
  rosterDriverIds: string[]
) => {
  if (rounds.length === 0) {
    return { rate: undefined, breakdown: "0/0" };
  }

  const numberOfRounds = rounds.length;
  const numberOfAce = statRoundsTeamAceCount(rounds, rosterDriverIds);
  const rate = numberOfAce / numberOfRounds;
  const breakdown = `${numberOfAce}/${numberOfRounds}`;
  return { rate, breakdown };
};
